import React, { useState, useEffect } from 'react';
import { GoShieldCheck } from 'react-icons/go';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Avatar, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Select, SelectItem } from '@nextui-org/react';
import { BsKey, BsThreeDotsVertical } from 'react-icons/bs';
import { RiDeleteBinLine } from 'react-icons/ri';
import Notiflix from 'notiflix';
import { getProjectsTeam, postProjectsUserRole, postProjectsUserDelete } from '../../../services/projects';

const TeamComponent = ({ projectId }) => {
  const [teamData, setTeamData] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getProjectsTeam({ slug: projectId });
      
      // API'den gelen veri yapısına göre ayır
      setTeamData(response.usersData || []);
      setCurrentUser(response.currentUser || null);
      
    } catch (error) {
      setError('Takım bilgileri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchTeamData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshTrigger]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 dark:text-red-400">{error}</p>
      </div>
    );
  }

  // Mevcut kullanıcının admin olup olmadığını kontrol et
  const isCurrentUserAdmin = currentUser?.user_status === 11;

  const refreshTeamData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
      {teamData.length > 0 ? (
        teamData.map((member, index) => (
          <TeamMemberCard 
            key={index} 
            member={member} 
            isCurrentUserAdmin={isCurrentUserAdmin}
            currentUserId={currentUser?.user_id}
            projectId={projectId}
            onRefresh={refreshTeamData}
          />
        ))
      ) : (
        <div className="col-span-full text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">Takım üyesi bulunamadı.</p>
        </div>
      )}
    </div>
  );
};

const TeamMemberCard = ({ member, isCurrentUserAdmin, currentUserId, projectId, onRefresh }) => {
  // Bu kullanıcının admin olup olmadığını kontrol et
  const isMemberAdmin = member.pstatus === 11;
  // Mevcut kullanıcının kendi kartı olup olmadığını kontrol et
  const isOwnCard = member.puid === currentUserId;
  
  // Modal state'leri
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(isMemberAdmin ? "11" : "1");

  // Rol değiştirme modalını aç
  const handleOpenRoleModal = () => {
    setSelectedRole(isMemberAdmin ? "11" : "1");
    setIsRoleModalOpen(true);
  };

  // Yetkileri değiştir
  const handleChangePermissions = async () => {
    const roleValue = parseInt(selectedRole);
    const roleText = roleValue === 11 ? 'Admin' : 'User';
    
    try {
      Notiflix.Loading.pulse('Yetkiler güncelleniyor...');
      const response = await postProjectsUserRole({
        slug: projectId,
        uid: member.id,
        role: roleValue
      });
      
      Notiflix.Loading.remove();
      setIsRoleModalOpen(false);
      
      // API'den gelen mesajı kullan
      if (response && response.message) {
        Notiflix.Notify.success(response.message);
      } else {
        Notiflix.Notify.success(`Kullanıcı rolü ${roleText} olarak güncellendi!`);
      }
      
      // Takım listesini yenile
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      Notiflix.Loading.remove();
      setIsRoleModalOpen(false);
      
      // Hata mesajını göster
      const errorMessage = error?.response?.data?.message || 'Yetkiler güncellenirken bir hata oluştu!';
      Notiflix.Notify.failure(errorMessage);
    }
  };

  // Projeden kaldır
  const handleRemoveFromProject = async () => {
    Notiflix.Confirm.show(
      'Projeden Kaldır',
      `${member.ufname} ${member.ulname} kullanıcısını projeden kaldırmak istediğinize emin misiniz?`,
      'Evet',
      'Hayır',
      async () => {
        try {
          Notiflix.Loading.pulse('Kullanıcı projeden kaldırılıyor...');
          
          const response = await postProjectsUserDelete({
            slug: projectId,
            id: member.id
          });
          
          Notiflix.Loading.remove();
          
          // API response'u string olduğu için direkt göster veya varsayılan mesaj kullan
          if (typeof response === 'string' && response) {
            Notiflix.Notify.success(response);
          } else {
            Notiflix.Notify.success('Başarılı');
          }
          
          // Takım listesini yenile
          if (onRefresh) {
            onRefresh();
          }
        } catch (error) {
          Notiflix.Loading.remove();
          
          // Hata mesajını göster
          const errorMessage = error?.response?.data?.message || 
                               error?.response?.data || 
                               'Kullanıcı kaldırılırken bir hata oluştu!';
          Notiflix.Notify.failure(typeof errorMessage === 'string' ? errorMessage : 'Kullanıcı kaldırılırken bir hata oluştu!');
        }
      }
    );
  };

  return (
    <div className="border justify-center items-center dark:border-dark-300 w-full border-gray-200 bg-white dark:bg-dark-300 h-full mt-6 rounded-md">
      {/* Admin ise ve kendi kartı değilse dropdown göster */}
      {isCurrentUserAdmin && !isOwnCard && (
        <div className="flex justify-end">
          <Dropdown className="">
            <DropdownTrigger>
              <Button className="bg-white dark:bg-dark-300">
                <BsThreeDotsVertical size={20} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Static Actions" onAction={(key) => {
              if (key === 'permissions') {
                handleOpenRoleModal();
              } else if (key === 'delete') {
                handleRemoveFromProject();
              }
            }}>
              <DropdownItem key="permissions">
                <div className="flex">
                  <BsKey size={20} />
                  <p className="font-semibold ml-2">İzinler</p>
                </div>
                <p className="ml-6">Yetkileri Değiştir</p>
              </DropdownItem>
              <DropdownItem key="delete" className="text-danger" color="danger">
                <div className="flex">
                  <RiDeleteBinLine size={20} />
                  <p className="font-semibold ml-2">Kaldır</p>
                </div>
                <p className="ml-6">Projeden Çıkart</p>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}
      
      <div className="flex flex-col justify-center items-center w-full p-5">
        <Avatar
          src={member.uimg !== null ? member.uimg : process.env.REACT_APP_USERIMG_BLANK}
          className="border-[1px] h-16 w-16"
        />
        <div className="flex flex-row items-center">
          <p className="dark:text-gray-400 font-semibold text-md mt-2">
            {`${member.ufname} ${member.ulname}`}
          </p>
          {/* Admin ise kırmızı shield göster */}
          {isMemberAdmin && (
            <GoShieldCheck className="text-red-600 dark:text-[#686AC9] text-xl ml-2 mt-2" />
          )}
        </div>
        {/* İş unvanı varsa göster */}
        {member.uwork && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
            {member.uwork}
          </p>
        )}
        {/* Kendi kartı ise "Siz" etiketi göster */}
        {isOwnCard && (
          <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full mt-1">
            Siz
          </span>
        )}
      </div>

      {/* Rol Değiştirme Modal */}
      <Modal 
        isOpen={isRoleModalOpen} 
        onClose={() => setIsRoleModalOpen(false)}
        placement="center"
        className="dark:bg-dark-200"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1 dark:text-white">
            Kullanıcı Rolünü Değiştir
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {`${member.ufname} ${member.ulname}`} için yeni rol seçin:
            </p>
            <Select
              label="Rol Seçin"
              placeholder="Bir rol seçin"
              selectedKeys={[selectedRole]}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full"
              classNames={{
                label: "dark:text-gray-300",
                value: "dark:text-white",
                trigger: "dark:bg-dark-300 dark:border-dark-400"
              }}
            >
              <SelectItem key="11" value="11">
                Admin
              </SelectItem>
              <SelectItem key="1" value="1">
                User
              </SelectItem>
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button 
              color="danger" 
              variant="light" 
              onPress={() => setIsRoleModalOpen(false)}
            >
              İptal
            </Button>
            <Button 
              color="primary" 
              onPress={handleChangePermissions}
              className="bg-red-600 text-white"
            >
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
export default TeamComponent
